import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid2'
import Button from '@mui/material/Button'
import defaultTexts from '../../../texts/texts'
import paypal_logo from '../../../assets/images/paypal_logo_150.png'
import stripe_logo from '../../../assets/images/stripe_logo_150.png'
import constants from '../../../config/constants'
import { request } from '../../../_network/request'
import { billingAccountConfigsURLs } from '../../../_network/apiUrls'
import CustomCheckboxField from '../../_common/_form_fields/CustomCheckboxField'
import CustomTextField from '../../_common/_form_fields/CustomTextField'
import CustomBackdrop from '../../_common/CustomBackdrop'

const PaymentMethodsEditItem = (props) => {
  const { name, display_name, paymentMethods, setFetchAccountConfigs } = props
  const theme = useTheme()

  const account_id = useSelector(state => state.profile.account_id)
  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [paymentChecked, setPaymentChecked] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [method, setMethod] = useState('GET')
  const [paymentId, setPaymentId] = useState('')
  const [showPaymentId, setShowPaymentId] = useState(false)
  const [showSave, setShowSave] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [paymentIdError, setPaymentIdError] = useState(false)

  const selectionWrapperStyle = {
    background: theme.palette.cultured,
    padding: '20px',
    border: `${theme.palette.lightSilver} 1px solid`,
    borderRadius: '20px',
    margin: '10px 0px',
  }

  const checkboxStyle = {
    color: theme.palette.violetsAreBlue,

    '&.Mui-checked': {
      color: theme.palette.violetsAreBlue,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  }

  const payWithTextStyle = {
    color: theme.palette.hanBlue,
    fontSize: '20px',
    fontWeight: '600',
    fontStyle: 'italic',
  }

  const imageStyle = {
    maxWidth: 'auto',
    maxHeight: '44px',
  }

  const formControlStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.vodka,
    borderRadius: '5px',
    width: '100%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.vodka,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
  }

  const saveButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '30px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const editButtonStyle = {
    fontWeight: '500',
    color: theme.palette.oxfordBlue,
    fontSize: '16px',
    textTransform: 'capitalize',
    textDecoration: 'underline',

    '&:hover': {
      background: 'none',
      textDecoration: 'none',
    },
  }

  const getCredentials = (name) => {
    if (name === 'paypal' && paymentId) {
      return { client_id: paymentId }
    }

    if (name === 'stripe' && paymentId) {
      return { secret_key: paymentId }
    }

    return
  }

  const billingAccountConfigsRequest = async (data) => {
    const url = method === 'DELETE' ?
    constants.billing_url + '/' + billingAccountConfigsURLs.getAccountPaymentMethod + '/?name=' + name :
    constants.billing_url + '/' + billingAccountConfigsURLs.getAccountPaymentMethod

    const headers = { 'customer-account-id': account_id }

    const request_data = data ? { url, method, data, headers } : { url, method, headers }
    
    await request(request_data)
  }

  const handleClickEdit = () => {
    setShowPaymentId(true)
  }

  const handleClickSave = async () => {
    setIsLoading(true)

    if ((method === 'POST' || method === 'PUT') && !paymentId) {
      setPaymentIdError(true)
    } else {
      let postData = {}

      if (method === 'POST') {
        postData = {
          account_id,
          name,
          display_name,
          credentials: getCredentials(name),
        }
      }

      if (method === 'PUT') {
        postData = {
          account_id,
          name,
          credentials: getCredentials(name),
        }
      }

      await billingAccountConfigsRequest((method === 'POST' || method === 'PUT') ? postData : null)

      setFetchAccountConfigs(prev => prev + 1)
      setPaymentId('')
      setShowPaymentId(false)
    }

    setIsLoading(false)
  }

  const getPaymentIdLabel = () => {
    if (name === 'paypal') {
      return defaultTexts.paypalClientId
    }

    if (name === 'stripe') {
      return defaultTexts.stripeSecretKey
    }

    return ''
  }

  const getPaymentLogo = () => {
    if (name === 'paypal') {
      return paypal_logo
    }

    if (name === 'stripe') {
      return stripe_logo
    }

    return ''
  }

  const handleChangePaymentChecked = () => {
    setPaymentChecked(prev => !prev)
  }

  const handleChangePaymentId = (e) => {
    const value = e.target.value
    setPaymentId(value)
  }

  useEffect(() => {
    const _payment = paymentMethods.find(item => item.name === name)
    
    if (_payment) {
      setPaymentChecked(true)
      setIsActive(true)
    } else {
      setPaymentChecked(false)
      setIsActive(false)
    }
  }, [paymentMethods, name])

  useEffect(() => {
    let _method = 'GET'

    if (paymentChecked && isActive) {
      _method = 'PUT'
    } else if (paymentChecked && !isActive) {
      _method = 'POST'
      setShowPaymentId(true)
    } else if (!paymentChecked && isActive) {
      _method = 'DELETE'
      setShowPaymentId(false)
    } else if (!paymentChecked && !isActive) {
      setShowPaymentId(false)
    }

    setMethod(_method)
  }, [paymentChecked, isActive])

  useEffect(() => {
    setShowSave(editPermission && ( (!paymentChecked && isActive) || (paymentChecked && !isActive) || (paymentChecked && isActive && showPaymentId) ))
  }, [editPermission, paymentChecked, isActive, showPaymentId])

  return (
    <>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Grid
        container
        spacing={{ lg: 4, xl: 6 }}
        sx={{ borderBottom: `${theme.palette.lightSilver} 1px dotted` }}
      >
        <Grid size={{ lg: 6 }}>
          <Box sx={selectionWrapperStyle}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
              spacing={2}
            >
              <CustomCheckboxField
                checked={paymentChecked}
                handleChange={handleChangePaymentChecked}
                sx={checkboxStyle}
              />
              <Box sx={payWithTextStyle}>{defaultTexts.payWith}</Box>
              <Box>
                <img src={getPaymentLogo()} alt='' style={imageStyle} />
              </Box>
            </Stack>
          </Box>
        </Grid>
        <Grid sx={{ flexGrow: 1, minWidth: 0 }}>
          {
            editPermission &&
            <Box sx={{ paddingTop: '30px' }}>
              {
                showPaymentId &&
                <CustomTextField 
                  variant='outlined'
                  label={getPaymentIdLabel()}
                  currentValue={paymentId}
                  setCurrentValue={handleChangePaymentId}
                  required={true}
                  error={paymentIdError}
                  size='small'
                  sx={formControlStyle}
                  outlinedInputSx={outlinedInputStyle}
                />
              }

              {
                (!showPaymentId && paymentChecked) &&
                <Button
                  variant='text'
                  disableRipple
                  disableElevation
                  disabled={false}
                  onClick={handleClickEdit}
                  sx={editButtonStyle}
                >
                  {defaultTexts.edit + ' ' + getPaymentIdLabel()}
                </Button>
              }            
            </Box>
          }              
        </Grid>
        <Grid xs="auto">
          {
            showSave &&
            <Stack
              direction='row'
              justifyContent='flex-end'
            >
              <Button
                variant='contained'
                disableRipple
                disableElevation
                disabled={false}
                onClick={handleClickSave}
                sx={saveButtonStyle}
              >
                {defaultTexts.save}
              </Button>
            </Stack>            
          }
        </Grid>
      </Grid>
    </>
  )
}

export default PaymentMethodsEditItem